import * as THREE from 'three';
import type { CameraAim, CameraKeyframe, EasingKey, ShakeLayer, Vec3 } from './types.ts';

/**
 * Professional shot presets. Each preset is a generator that bakes into sparse,
 * editable keyframes (plus an optional live shake layer) — downstream code only
 * ever sees keyframes, so presets stay tweakable like hand-made cameras.
 */

export interface PresetParamSpec {
  key: string;
  label: string;
  min: number;
  max: number;
  step: number;
  default: number;
  /** Context-dependent default (e.g. "current orbit radius"); wins over `default`. */
  defaultFrom?: (ctx: PresetContext) => number;
  /** When set, rendered as segmented buttons instead of a slider. */
  options?: { value: number; label: string }[];
  unit?: string;
}

export interface PresetContext {
  /** Base camera pose (the free view on create; the first keyframe on re-bake). */
  position: THREE.Vector3;
  target: THREE.Vector3;
  fov: number;
  /** Subject anchor (chosen actor's hips at the playhead, else the orbit target). */
  subject: THREE.Vector3;
  subjectActorId: string | null;
  /** Marked range in seconds — presets span it by default. */
  startSec: number;
  endSec: number;
  /** Subject world position at an arbitrary time (used by the follow preset). */
  sampleSubject?: (sec: number) => THREE.Vector3 | null;
}

export interface PresetResult {
  keyframes: CameraKeyframe[];
  aim: CameraAim;
  shake: ShakeLayer | null;
  interp: 'smooth' | 'linear';
}

export interface CameraPreset {
  id: string;
  label: string;
  desc: string;
  params: PresetParamSpec[];
  generate: (ctx: PresetContext, params: Record<string, number>) => PresetResult;
}

const DEG2RAD = Math.PI / 180;

function key(time: number, position: THREE.Vector3, target: THREE.Vector3, fov: number, easing: EasingKey): CameraKeyframe {
  return {
    time,
    position: position.toArray() as Vec3,
    target: target.toArray() as Vec3,
    fov,
    easing,
  };
}

/** Aim at the subject's hips when an actor is chosen, else follow the baked targets. */
function subjectAim(ctx: PresetContext, bone = 'hips', heightOffset = 0): CameraAim {
  if (ctx.subjectActorId) {
    return { mode: 'bone', actorId: ctx.subjectActorId, bone, heightOffset };
  }
  return { mode: 'path' };
}

function shake(posAmp: number, rotAmp: number, freq: number): ShakeLayer {
  return { enabled: true, posAmp, rotAmp, freq, seed: 1 };
}

function horizontalDistance(a: THREE.Vector3, b: THREE.Vector3): number {
  const dx = a.x - b.x;
  const dz = a.z - b.z;
  return Math.max(0.5, Math.hypot(dx, dz));
}

/** Circle keyframes around the subject; a key per ≤45° keeps Catmull-Rom round. */
function orbitGenerate(ctx: PresetContext, params: Record<string, number>): PresetResult {
  const degrees = params.degrees ?? 360;
  const direction = params.direction ?? 1;
  const radius = params.radius ?? horizontalDistance(ctx.position, ctx.subject);
  const height = params.height ?? ctx.position.y;
  const a0 = Math.atan2(ctx.position.z - ctx.subject.z, ctx.position.x - ctx.subject.x);
  const steps = Math.max(2, Math.ceil(Math.abs(degrees) / 45));
  const keys: CameraKeyframe[] = [];
  const pos = new THREE.Vector3();
  for (let i = 0; i <= steps; i++) {
    const f = i / steps;
    const a = a0 + direction * degrees * DEG2RAD * f;
    pos.set(ctx.subject.x + Math.cos(a) * radius, height, ctx.subject.z + Math.sin(a) * radius);
    keys.push(key(ctx.startSec + (ctx.endSec - ctx.startSec) * f, pos, ctx.subject, ctx.fov, 'linear'));
  }
  return { keyframes: keys, aim: subjectAim(ctx), shake: null, interp: 'smooth' };
}

const ORBIT_PARAMS: PresetParamSpec[] = [
  {
    key: 'degrees',
    label: 'Sweep',
    min: 45,
    max: 720,
    step: 45,
    default: 360,
    unit: '°',
  },
  {
    key: 'direction',
    label: 'Direction',
    min: -1,
    max: 1,
    step: 2,
    default: 1,
    options: [
      { value: 1, label: 'Clockwise' },
      { value: -1, label: 'Counter' },
    ],
  },
  {
    key: 'radius',
    label: 'Radius',
    min: 0.5,
    max: 15,
    step: 0.1,
    default: 4,
    defaultFrom: (ctx) => +horizontalDistance(ctx.position, ctx.subject).toFixed(1),
    unit: 'm',
  },
  {
    key: 'height',
    label: 'Height',
    min: 0.1,
    max: 8,
    step: 0.1,
    default: 2,
    defaultFrom: (ctx) => +Math.max(0.1, ctx.position.y).toFixed(1),
    unit: 'm',
  },
];

function dollyLine(ctx: PresetContext, fromScale: number, toScale: number, easing: EasingKey): PresetResult {
  const dir = new THREE.Vector3().subVectors(ctx.subject, ctx.position);
  const start = ctx.position.clone().addScaledVector(dir, fromScale);
  const end = ctx.position.clone().addScaledVector(dir, toScale);
  return {
    keyframes: [
      key(ctx.startSec, start, ctx.subject, ctx.fov, easing),
      key(ctx.endSec, end, ctx.subject, ctx.fov, easing),
    ],
    aim: subjectAim(ctx),
    shake: null,
    interp: 'linear',
  };
}

export const PRESETS: CameraPreset[] = [
  {
    id: 'static',
    label: 'Static — current view',
    desc: 'A tripod locked to exactly what you see now.',
    params: [],
    generate: (ctx) => ({
      keyframes: [key(ctx.startSec, ctx.position, ctx.target, ctx.fov, 'ease')],
      aim: { mode: 'path' },
      shake: null,
      interp: 'smooth',
    }),
  },
  {
    id: 'orbit',
    label: 'Orbit 360°',
    desc: 'Full circle around the subject, starting from your angle.',
    params: ORBIT_PARAMS,
    generate: orbitGenerate,
  },
  {
    id: 'arc',
    label: 'Arc 180°',
    desc: 'Half-moon sweep around the subject — a slow reveal.',
    params: ORBIT_PARAMS.map((p) => (p.key === 'degrees' ? { ...p, default: 180 } : p)),
    generate: orbitGenerate,
  },
  {
    id: 'push-in',
    label: 'Push in',
    desc: 'Dolly from here toward the subject.',
    params: [
      { key: 'amount', label: 'Move closer', min: 10, max: 80, step: 5, default: 40, unit: '%' },
    ],
    generate: (ctx, p) => dollyLine(ctx, 0, (p.amount ?? 40) / 100, 'ease'),
  },
  {
    id: 'pull-out',
    label: 'Pull out',
    desc: 'Start close and retreat to reveal the scene.',
    params: [
      { key: 'amount', label: 'Start closer', min: 10, max: 80, step: 5, default: 40, unit: '%' },
    ],
    generate: (ctx, p) => dollyLine(ctx, (p.amount ?? 40) / 100, 0, 'ease'),
  },
  {
    id: 'dolly-zoom',
    label: 'Dolly zoom',
    desc: 'Vertigo effect: the subject holds still while the world stretches.',
    params: [
      { key: 'strength', label: 'Strength', min: 1.1, max: 2.5, step: 0.05, default: 1.6, unit: '×' },
      {
        key: 'direction',
        label: 'Move',
        min: -1,
        max: 1,
        step: 2,
        default: 1,
        options: [
          { value: 1, label: 'Push in' },
          { value: -1, label: 'Pull back' },
        ],
      },
    ],
    generate: (ctx, p) => {
      const strength = p.strength ?? 1.6;
      const dirSign = p.direction ?? 1;
      const f0 = ctx.fov;
      const f1 = THREE.MathUtils.clamp(dirSign > 0 ? f0 * strength : f0 / strength, 10, 120);
      const d0 = Math.max(0.5, ctx.position.distanceTo(ctx.subject));
      const back = new THREE.Vector3().subVectors(ctx.position, ctx.subject).normalize();
      const keys: CameraKeyframe[] = [];
      const steps = 8; // the fov↔distance relation is nonlinear — bake it densely
      const pos = new THREE.Vector3();
      const w0 = Math.tan((f0 * DEG2RAD) / 2) * d0; // subject size invariant
      for (let i = 0; i <= steps; i++) {
        const f = i / steps;
        const fov = f0 + (f1 - f0) * f;
        const d = w0 / Math.tan((fov * DEG2RAD) / 2);
        pos.copy(ctx.subject).addScaledVector(back, d);
        keys.push(key(ctx.startSec + (ctx.endSec - ctx.startSec) * f, pos, ctx.subject, fov, 'linear'));
      }
      return { keyframes: keys, aim: subjectAim(ctx), shake: null, interp: 'linear' };
    },
  },
  {
    id: 'jib-up',
    label: 'Jib / crane up',
    desc: 'Rise from low and drift back, like a crane arm lifting.',
    params: [
      { key: 'rise', label: 'Rise', min: 0.5, max: 5, step: 0.1, default: 2.5, unit: 'm' },
      { key: 'drift', label: 'Drift back', min: 0, max: 4, step: 0.1, default: 1.5, unit: 'm' },
    ],
    generate: (ctx, p) => craneKeys(ctx, p.rise ?? 2.5, p.drift ?? 1.5, 'up'),
  },
  {
    id: 'crane-down',
    label: 'Crane down',
    desc: 'Descend from high toward the subject.',
    params: [
      { key: 'rise', label: 'Drop', min: 0.5, max: 5, step: 0.1, default: 2.5, unit: 'm' },
      { key: 'drift', label: 'Drift in', min: 0, max: 4, step: 0.1, default: 1.5, unit: 'm' },
    ],
    generate: (ctx, p) => craneKeys(ctx, p.rise ?? 2.5, p.drift ?? 1.5, 'down'),
  },
  {
    id: 'pan',
    label: 'Pan',
    desc: 'Fixed tripod sweeping its gaze across the subject.',
    params: [
      { key: 'degrees', label: 'Sweep', min: 20, max: 160, step: 5, default: 60, unit: '°' },
      {
        key: 'direction',
        label: 'Direction',
        min: -1,
        max: 1,
        step: 2,
        default: 1,
        options: [
          { value: 1, label: 'Left → right' },
          { value: -1, label: 'Right → left' },
        ],
      },
    ],
    generate: (ctx, p) => {
      const deg = ((p.degrees ?? 60) / 2) * DEG2RAD;
      const dir = p.direction ?? 1;
      const dist = Math.max(0.5, horizontalDistance(ctx.position, ctx.subject));
      const a0 = Math.atan2(ctx.subject.z - ctx.position.z, ctx.subject.x - ctx.position.x);
      const targetAt = (a: number): THREE.Vector3 =>
        new THREE.Vector3(
          ctx.position.x + Math.cos(a) * dist,
          ctx.subject.y,
          ctx.position.z + Math.sin(a) * dist
        );
      const mid = (ctx.startSec + ctx.endSec) / 2;
      return {
        // The camera never moves — only its look-at target sweeps.
        keyframes: [
          key(ctx.startSec, ctx.position, targetAt(a0 - dir * deg), ctx.fov, 'ease-in'),
          key(mid, ctx.position, targetAt(a0), ctx.fov, 'ease-out'),
          key(ctx.endSec, ctx.position, targetAt(a0 + dir * deg), ctx.fov, 'ease'),
        ],
        aim: { mode: 'path' }, // aim-tracking would defeat the pan
        shake: null,
        interp: 'smooth',
      };
    },
  },
  {
    id: 'handheld',
    label: 'Concert handheld',
    desc: 'Shoulder-cam drift and shake, like a crowd shooter.',
    params: [
      { key: 'drift', label: 'Drift', min: 0, max: 100, step: 5, default: 40, unit: '%' },
    ],
    generate: (ctx, p) => {
      const drift = (p.drift ?? 40) / 100;
      const mid = (ctx.startSec + ctx.endSec) / 2;
      const o1 = new THREE.Vector3(0.3 * drift, 0.08 * drift, -0.2 * drift);
      const o2 = new THREE.Vector3(-0.22 * drift, -0.05 * drift, 0.26 * drift);
      const chest = ctx.subject.clone();
      chest.y += 0.35;
      return {
        keyframes: [
          key(ctx.startSec, ctx.position, chest, ctx.fov, 'ease'),
          key(mid, ctx.position.clone().add(o1), chest, ctx.fov, 'ease'),
          key(ctx.endSec, ctx.position.clone().add(o2), chest, ctx.fov, 'ease'),
        ],
        aim: subjectAim(ctx, 'spine'),
        shake: shake(0.05, 1.5, 1.8),
        interp: 'smooth',
      };
    },
  },
  {
    id: 'selfie',
    label: 'Selfie',
    desc: 'Arm’s-length phone shot, slightly above eye level.',
    params: [
      { key: 'distance', label: 'Distance', min: 0.4, max: 1.5, step: 0.05, default: 0.7, unit: 'm' },
    ],
    generate: (ctx, p) => {
      const dist = p.distance ?? 0.7;
      const dir = new THREE.Vector3().subVectors(ctx.position, ctx.subject);
      dir.y = 0;
      if (dir.lengthSq() < 1e-4) dir.set(0, 0, 1);
      dir.normalize();
      const head = ctx.subject.clone();
      head.y += 0.6;
      const at = (angle: number): THREE.Vector3 => {
        const rotated = dir.clone().applyAxisAngle(new THREE.Vector3(0, 1, 0), angle);
        const pos = head.clone().addScaledVector(rotated, dist);
        pos.y += 0.18; // slightly above the head, angled down — classic selfie
        return pos;
      };
      return {
        keyframes: [
          key(ctx.startSec, at(-0.12), head, 65, 'ease'),
          key(ctx.endSec, at(0.12), head, 65, 'ease'),
        ],
        aim: subjectAim(ctx, 'head'),
        shake: shake(0.02, 1.2, 2.2),
        interp: 'smooth',
      };
    },
  },
  {
    id: 'tiktok',
    label: 'Vertical follow',
    desc: 'Phone-style framing that keeps the subject centered (best in 9:16).',
    params: [
      { key: 'distance', label: 'Distance', min: 1, max: 4, step: 0.1, default: 2.2, unit: 'm' },
    ],
    generate: (ctx, p) => {
      const dist = p.distance ?? 2.2;
      const dir = new THREE.Vector3().subVectors(ctx.position, ctx.subject);
      dir.y = 0;
      if (dir.lengthSq() < 1e-4) dir.set(0, 0, 1);
      dir.normalize();
      const pos = ctx.subject.clone().addScaledVector(dir, dist);
      pos.y = ctx.subject.y + 0.35;
      const chest = ctx.subject.clone();
      chest.y += 0.3;
      return {
        keyframes: [key(ctx.startSec, pos, chest, 55, 'ease')],
        aim: subjectAim(ctx, 'hips', 0.3),
        shake: shake(0.015, 0.8, 1.6),
        interp: 'smooth',
      };
    },
  },
  {
    id: 'follow',
    label: 'Follow / track',
    desc: 'Travels with the subject, keeping your current framing.',
    params: [
      { key: 'keys', label: 'Waypoints', min: 4, max: 16, step: 1, default: 8 },
    ],
    generate: (ctx, p) => {
      const n = Math.max(2, Math.round(p.keys ?? 8));
      const offset = new THREE.Vector3().subVectors(ctx.position, ctx.subject);
      const keys: CameraKeyframe[] = [];
      const pos = new THREE.Vector3();
      for (let i = 0; i <= n; i++) {
        const f = i / n;
        const t = ctx.startSec + (ctx.endSec - ctx.startSec) * f;
        const s = ctx.sampleSubject?.(t) ?? ctx.subject;
        pos.copy(s).add(offset);
        keys.push(key(t, pos, s, ctx.fov, 'linear'));
      }
      return { keyframes: keys, aim: subjectAim(ctx), shake: null, interp: 'smooth' };
    },
  },
];

function craneKeys(ctx: PresetContext, rise: number, drift: number, dir: 'up' | 'down'): PresetResult {
  const back = new THREE.Vector3().subVectors(ctx.position, ctx.subject);
  back.y = 0;
  if (back.lengthSq() < 1e-4) back.set(0, 0, 1);
  back.normalize();

  const low = ctx.position.clone();
  low.y = Math.max(0.3, ctx.subject.y - 0.5);
  const high = low.clone().addScaledVector(back, drift);
  high.y = low.y + rise;

  const from = dir === 'up' ? low : high;
  const to = dir === 'up' ? high : low;
  const mid = from.clone().lerp(to, 0.5);
  const midTime = (ctx.startSec + ctx.endSec) / 2;
  return {
    keyframes: [
      key(ctx.startSec, from, ctx.subject, ctx.fov, 'ease'),
      key(midTime, mid, ctx.subject, ctx.fov, 'ease-out'),
      key(ctx.endSec, to, ctx.subject, ctx.fov, 'ease-out'),
    ],
    aim: subjectAim(ctx, dir === 'up' ? 'head' : 'hips'),
    shake: null,
    interp: 'smooth',
  };
}

export function getPreset(id: string): CameraPreset | null {
  return PRESETS.find((p) => p.id === id) ?? null;
}

/** Initial param values for a preset in a given context. */
export function defaultParams(preset: CameraPreset, ctx: PresetContext): Record<string, number> {
  const out: Record<string, number> = {};
  for (const spec of preset.params) {
    out[spec.key] = spec.defaultFrom ? spec.defaultFrom(ctx) : spec.default;
  }
  return out;
}
